import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { convsAPI } from '../services/api';
import { Search, X, MessageSquare, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

function ConvModal({ conv, onClose }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-header">
          <span className="modal-title">Conversation</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span>Bot: {conv.bot?.name || '—'}</span>
          <span>Started: {format(new Date(conv.createdAt), 'MMM d, yyyy HH:mm')}</span>
          {conv.visitor?.page && <span>Page: {conv.visitor.page}</span>}
          {conv.rating && <span>⭐ {conv.rating}/5</span>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 400, overflowY: 'auto', padding: '4px 0' }}>
          {conv.messages?.filter(m => m.role !== 'system').map((m, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '80%', padding: '9px 14px', borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px', background: m.role === 'user' ? 'var(--brand)' : 'var(--surface2)', color: m.role === 'user' ? '#fff' : 'var(--text)', fontSize: 13, lineHeight: 1.6 }}>
                {m.content}
              </div>
              <span style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{format(new Date(m.timestamp), 'HH:mm')}</span>
            </div>
          ))}
        </div>
        {conv.lead?.captured && (
          <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(26,158,92,0.08)', borderRadius: 'var(--radius)', border: '1px solid rgba(26,158,92,0.2)', fontSize: 13 }}>
            ✅ Lead captured: {conv.lead.name} · {conv.lead.email} {conv.lead.phone && `· ${conv.lead.phone}`}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Conversations() {
  const [convs, setConvs]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    convsAPI.list({ page, limit: 20, search: search || undefined })
      .then(r => { setConvs(r.data.conversations || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error('Failed to load conversations'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const open = async (conv) => {
    try {
      const { data } = await convsAPI.get(conv._id);
      setSelected(data.conversation);
    } catch { toast.error('Failed to load conversation'); }
  };

  const del = async (conv, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await convsAPI.delete(conv._id);
      setConvs(c => c.filter(x => x._id !== conv._id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const pages = Math.ceil(total / 20);

  return (
    <div>
      <div className="page-header">
        <h1>Conversations</h1>
        <p>{total} total conversations</p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ position: 'relative', maxWidth: 360 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
          <input className="input" placeholder="Search conversations…" style={{ paddingLeft: 36 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>
        ) : convs.length === 0 ? (
          <div className="empty-state">
            <MessageSquare size={40} style={{ color: 'var(--text3)' }} />
            <h3>No conversations yet</h3>
            <p>Conversations will appear here once visitors start chatting with your bots.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Bot</th><th>Visitor</th><th>Messages</th><th>Lead</th><th>Status</th><th>Date</th><th></th></tr>
              </thead>
              <tbody>
                {convs.map(c => (
                  <tr key={c._id} style={{ cursor: 'pointer' }} onClick={() => open(c)}>
                    <td style={{ fontWeight: 500, fontSize: 13 }}>{c.bot?.name || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>
                      {c.lead?.name || c.visitor?.ip || 'Anonymous'}
                      {c.lead?.email && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.lead.email}</div>}
                    </td>
                    <td style={{ fontSize: 13 }}>{c.messages?.length || 0}</td>
                    <td>
                      {c.lead?.captured
                        ? <span className="badge badge-green">Captured</span>
                        : <span className="badge badge-gray">None</span>}
                    </td>
                    <td><span className={`badge badge-${c.status === 'active' ? 'blue' : 'gray'}`}>{c.status}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{format(new Date(c.createdAt), 'MMM d, HH:mm')}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--red)' }} onClick={e => del(c, e)}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '14px 20px', borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={15} /></button>
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>Page {page} of {pages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page === pages} onClick={() => setPage(p => p + 1)}><ChevronRight size={15} /></button>
          </div>
        )}
      </div>

      {selected && <ConvModal conv={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
